import React, { useEffect, useState, useRef } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import { callAPI } from "../utils/apiUtils";
import { apiUrls } from "../utils/apiUrls";
import { toast } from "react-toastify";
import ReservationDetailModal from "../components/modals/ReservationDetailModal";
import ValidatePinModal from "../components/modals/ValidatePinModal";
import moment from "moment";
import '../styles/reservationcalnder.css';

// Marks the per-card delete button. handleEventClick reads this off the click target to tell a
// delete tap apart from a tap on the card itself — see the comment there.
const DELETE_BTN_CLASS = "fc-reservation-delete";

const CalendarView = ({ focusDate, onAddBooking }) => {
  const calendarRef = useRef(null);
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  // { id, label } for the booking awaiting manager authorisation, or null when no PIN prompt is up.
  const [deleteTarget, setDeleteTarget] = useState(null);
  // Bumped on every rejected PIN so the modal remounts: ValidatePinModal only clears its digit
  // boxes when `isOpen` flips, so without a fresh key a second attempt would start with the
  // previous (wrong) four digits still sitting in the inputs.
  const [pinAttempt, setPinAttempt] = useState(0);

  const fetchEvents = async () => {
    try {
      const resp = await callAPI(apiUrls.RESERVATION_LISTING_CAL, {}, "GET");
      const rawEvents = resp.data.events || resp.data;

      // Keep the real timestamp on `start` (not date-only) so timeGrid week/day views can place
      // each booking at its actual hour instead of dumping everything into the all-day row.
      // bookingTime is still derived here purely for the custom bold time label in
      // renderEventContent — month view's default eventOrder sorts correctly on its own now that
      // `start` carries the real time instead of an identical date-only value for every event.
      const normalizedEvents = rawEvents.map(ev => {
        const timePart = ev.start.split("T")[1];

        return {
          ...ev,
          allDay: false,
          extendedProps: {
            ...ev.extendedProps,
            bookingTime: timePart
              ? moment(timePart.split("-")[0], "HH:mm:ss").format("hh:mm A")
              : "",
            _dateKey: ev.start.split("T")[0],
            _hour: timePart ? parseInt(timePart.split(":")[0], 10) : null
          }
        };
      });

      // Flag, per day, the first morning and first afternoon/evening booking so the month view
      // can drop a lightweight AM/PM divider between them — this groups the day cell's
      // already-chronologically-sorted events into two visual sections without building a
      // second list layout for it.
      const seenPeriodByDate = {};
      normalizedEvents
        .slice()
        .sort((a, b) => new Date(a.start) - new Date(b.start))
        .forEach(ev => {
          const { _dateKey, _hour } = ev.extendedProps;
          if (_hour === null) return;
          const period = _hour < 12 ? "AM" : "PM";
          seenPeriodByDate[_dateKey] = seenPeriodByDate[_dateKey] || {};
          if (!seenPeriodByDate[_dateKey][period]) {
            seenPeriodByDate[_dateKey][period] = true;
            ev.extendedProps.periodMarker = period;
          }
        });

      setEvents(normalizedEvents);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load calendar events");
    }
  };

  useEffect(() => {
    fetchEvents();
  }, []);

  useEffect(() => {
    if (focusDate && calendarRef.current) {
      const api = calendarRef.current.getApi();
      api.gotoDate(focusDate);
      fetchEvents();
    }
  }, [focusDate]);


  /**
   * Per-card delete affordance. Deliberately NOT hidden behind the logged-in user's own
   * permissions: a server who spots a duplicate booking should be able to start the deletion and
   * have a manager authorise it at the PIN prompt, which is the whole point of the PIN. Authority
   * is decided server-side from whoever's PIN is entered.
   *
   * Always visible rather than revealed on hover — the POS runs on Android tablets where there is
   * no hover state. An accidental tap only opens the PIN prompt, so it stays recoverable.
   */
  const renderDeleteButton = (eventInfo) => (
    <button
      type="button"
      className={DELETE_BTN_CLASS}
      title="Delete booking"
      aria-label={`Delete booking for ${eventInfo.event.extendedProps.name || eventInfo.event.title}`}
      // preventDefault, and deliberately NOT stopPropagation. FullCalendar renders an event that
      // has a `url` as a real <a href>, so the click has to be defaulted-out or the browser
      // navigates to the guest's booking page. stopPropagation() looks like the natural way to
      // keep the tap from also opening the detail modal, but it actively breaks things:
      // FullCalendar delegates its click handling high up the tree, so React's root-level handler
      // runs *first* — stopping propagation there means FullCalendar's handler never runs, which
      // means handleEventClick's own preventDefault() never fires either, and the anchor navigates
      // away. Suppressing the detail modal is handled inside handleEventClick instead.
      onClick={(e) => {
        e.preventDefault();
        requestDelete(eventInfo);
      }}
    >
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path
          d="M4 7h16M9 7V5a1 1 0 011-1h4a1 1 0 011 1v2m-9 0l1 13a1 1 0 001 1h6a1 1 0 001-1l1-13"
          stroke="currentColor"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </button>
  );

  const renderEventContent = (eventInfo) => {
    const { bookingTime, guest, name, tableId, periodMarker } = eventInfo.event.extendedProps;
    // The API sends the literal string 'Not Assigned' (not null/empty) when no table is set -
    // only render the badge once a real table name is present.
    const assignedTable = tableId && tableId !== "Not Assigned" ? tableId : null;

    // Week/day columns are narrow and events can sit side-by-side when times overlap — the
    // month view's 3-line card doesn't fit there, so timeGrid gets a compact three-line version
    // (time on its own row, title + guest count, then the guest's name) instead of one cramped
    // single line that ellipsizes as soon as a few bookings share a start time.
    if (eventInfo.view.type !== "dayGridMonth") {
      return (
        <div className="fc-reservation-compact">
          <div className="fc-reservation-compact-row1">
            {bookingTime && <span className="fc-reservation-compact-time">{bookingTime}</span>}
            {renderDeleteButton(eventInfo)}
          </div>
          <div className="fc-reservation-compact-row2">
            <span className="fc-reservation-compact-title">{eventInfo.event.title}</span>
            {guest && <span className="fc-reservation-guest">{guest}</span>}
            {assignedTable && <span className="fc-reservation-table">Table {assignedTable}</span>}
          </div>
          {name && <div className="fc-reservation-compact-name">{name}</div>}
        </div>
      );
    }

    return (
      <>
        {periodMarker && (
          <div className="fc-period-divider">{periodMarker}</div>
        )}
        <div className="fc-reservation-card" style={{ fontSize: "12px", lineHeight: "1.25" }}>
          {renderDeleteButton(eventInfo)}

          {bookingTime && (
            <div style={{ fontWeight: "600" }}>{bookingTime}</div>
          )}

          <div>
            <strong>{eventInfo.event.title}</strong>
            {guest && <span className="fc-reservation-guest">{guest}</span>}
            {assignedTable && <span className="fc-reservation-table">Table {assignedTable}</span>}
          </div>

          <div style={{ fontSize: "11px", opacity: 0.85 }}>
            {name}
          </div>
        </div>
      </>
    );
  };

  // 5 se jyada hai to laal kar do
  const eventClassNames = (arg) => {
    const guest = parseInt(arg.event.extendedProps?.guest, 10);
    if (guest > 5) return ["fc-event-danger"];
    return [];
  };


  const handleEventDidMount = (info) => {
    const desc = info.event.extendedProps?.description;
    if (desc) info.el.setAttribute("title", desc);
  };



  const handleEventClick = (info) => {
    info.jsEvent.preventDefault();

    // A tap on the card's delete button must not also open the booking detail modal. This click
    // still reaches FullCalendar (the button's handler must not stopPropagation — see the comment
    // there for why), so filtering it out here, by asking where the click actually originated, is
    // what keeps the two actions separate.
    if (info.jsEvent.target.closest(`.${DELETE_BTN_CLASS}`)) {
      return;
    }

    const ext = info.event.extendedProps || {};

    setSelectedEvent({
      id: ext.reservationId,
      title: info.event.title,
      start: info.event.start,
      customer: ext.name || "N/A",
      phone: ext.contact || "N/A",
      time: ext.bookingTime || "N/A",
      guests: ext.guest || "N/A",
      type: ext.source || "N/A",
      assignedTable: ext.tableId || null,
      status: ext.status || null,
      noShowCount: ext.noShowCount || 0,
      profileUrl: info.event.url,
    });

    setIsModalOpen(true);
  };


  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedEvent(null);
  };

  const updateBookingStatus = async (id, status) => {
    try {
      const res = await callAPI(
        apiUrls.RESERVATION_UPDATE_STATUS,
        {},
        "POST",
        { id, status },
        {}
      );

      if (res.status === 1 || res.data?.status === 1) {
        toast.success(res.message || "Status updated");
        fetchEvents();
        handleCloseModal();
      } else {
        toast.error(res.message || "Failed to update");
      }
    } catch (err) {
      console.error(err);
      toast.error("Error updating status");
    }
  };
  const clearNoShows = async (mobile) => {
    try {
      const res = await callAPI(
        apiUrls.RESERVATION_CLEAR_NO_SHOWS,
        {},
        "POST",
        { mobile },
        {}
      );

      if (res.status === 1 || res.data?.status === 1) {
        toast.success(res.message || "No-show count cleared");
        setSelectedEvent((prev) => (prev ? { ...prev, noShowCount: 0 } : prev));
        fetchEvents();
      } else {
        toast.error(res.message || "Failed to clear no-show count");
      }
    } catch (err) {
      console.error(err);
      toast.error("Error clearing no-show count");
    }
  };

  const sendReminder = async (id) => {
    try {
      const res = await callAPI(
        apiUrls.RESERVATION_SEND_REMINDER,
        {},
        "POST",
        { id },
        {}
      );

      if (res.status === 1 || res.data?.status === 1) {
        toast.success(res.message || "Reminder SMS sent");
      } else {
        toast.error(res.message || "Failed to send reminder");
      }
    } catch (err) {
      console.error(err);
      toast.error("Error sending reminder");
    }
  };

  const reservationCancel = async (id) => {
    try {
      const res = await callAPI(
        apiUrls.RESERVATION_CANCEL,
        {},
        "POST",
        { id },
        {}
      );

      if (res.status === 1 || res.data?.status === 1) {
        toast.success(res.message || "Reservation cancelled");
        fetchEvents();
        handleCloseModal();
      } else {
        toast.error(res.message || "Failed to cancel reservation");
      }
    } catch (err) {
      console.error(err);
      toast.error("Error cancelling reservation");
    }
  };

  const requestDelete = (eventInfo) => {
    const ext = eventInfo.event.extendedProps || {};
    const reservationId = ext.reservationId;

    if (!reservationId) {
      toast.error("This booking couldn't be identified — please refresh the calendar.");
      return;
    }

    // Name the booking in the prompt so a manager typing their PIN can see what they're
    // authorising, rather than confirming an anonymous "are you sure?".
    const parts = [
      ext.name,
      ext.bookingTime,
      eventInfo.event.start ? moment(eventInfo.event.start).format("D MMM") : null,
      ext.guest ? `${ext.guest} guest${Number(ext.guest) === 1 ? "" : "s"}` : null,
    ].filter(Boolean);

    setPinAttempt((n) => n + 1);
    setDeleteTarget({ id: reservationId, label: parts.join(" · ") });
  };

  const handleDeleteConfirm = async (pin) => {
    if (!deleteTarget) return;

    try {
      const res = await callAPI(
        apiUrls.RESERVATION_DELETE_BOOKING,
        {},
        "POST",
        { id: deleteTarget.id, pin },
        {}
      );

      const payload = res.data || {};

      if (payload.status === 1) {
        toast.success(
          payload.deleted_by
            ? `Booking deleted (authorised by ${payload.deleted_by})`
            : payload.message || "Booking deleted"
        );
        setDeleteTarget(null);
        // The detail modal may be open on the very booking just deleted.
        handleCloseModal();
        fetchEvents();
        return;
      }

      // Wrong PIN or an unauthorised staff member: keep the prompt up so a manager can try
      // again, but remount it so the digit boxes start empty.
      toast.error(payload.message || "Could not delete booking");
      setPinAttempt((n) => n + 1);
    } catch (err) {
      console.error(err);
      toast.error("Error deleting booking");
      setPinAttempt((n) => n + 1);
    }
  };

  const handleAccept = () => updateBookingStatus(selectedEvent.id, 2);
  const handleReject = () => updateBookingStatus(selectedEvent.id, 0);
  /**
   * FullCalendar hands back a Date at LOCAL midnight for the cell. toISOString() would convert that
   * to UTC and, anywhere east of Greenwich, hand back the previous day - so the parts are read off
   * the local date directly. The result is the YYYY-MM-DD the booking form's date input expects.
   */
  const toLocalDateString = (date) => {
    const pad = (n) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  };

  /**
   * Day-cell content: the usual date number, plus an add button that opens Create New with this
   * date already filled.
   *
   * Shown on every day, including past ones. Suppressing past days was worse in practice: a month
   * view almost always opens on weeks that have already been, so most of the grid came up with no
   * button at all and simply read as broken. Past days are dimmed instead, and the booking form's
   * own date input still carries min={today}, which is the guard that actually prevents a
   * back-dated booking - the same one that already applies to a hand-typed date.
   *
   * No stopPropagation here on purpose. This calendar has no dateClick handler for the button to
   * conflict with, and swallowing the event higher up is what previously stopped FullCalendar
   * calling preventDefault on its own anchors - see the delete-button comment above.
   */
  const renderDayCell = (arg) => (
    <div className="fcDayCellTop">
      <span className="fcDayCellNumber">{arg.dayNumberText}</span>
      {onAddBooking && (
        <button
          type="button"
          className={`fcDayAddBtn${arg.isPast ? ' fcDayAddBtnPast' : ''}`}
          title={`Add a booking on ${toLocalDateString(arg.date)}`}
          aria-label={`Add a booking on ${toLocalDateString(arg.date)}`}
          onClick={() => onAddBooking(toLocalDateString(arg.date))}
        >
          Add
        </button>
      )}
    </div>
  );

  const handleCancel = () => reservationCancel(selectedEvent.id);
  const handleNoShow = () => updateBookingStatus(selectedEvent.id, 4);
  const handleClearNoShows = () => clearNoShows(selectedEvent.phone);
  const handleSendReminder = () => sendReminder(selectedEvent.id);


  return (
    <>
      <FullCalendar
        ref={calendarRef}
        plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
        initialView="timeGridDay"
        timeZone="local"
        dayMaxEvents={4}
        headerToolbar={{
          left: "prev,next today",
          center: "title",
          right: "dayGridMonth,timeGridWeek,timeGridDay"
        }}
        events={events}
        eventDisplay="block"
        slotDuration="00:30:00"
        slotLabelInterval="01:00:00"
        // Week/day view defaulted to the full 00:00-24:00 range, so the handful of hours
        // restaurants actually take bookings in (see the booking-time dropdown, which never
        // offers anything before 11am) got squeezed into a sliver of the visible height along
        // with a dozen empty overnight slots nobody scrolls to.
        slotMinTime="08:00:00"
        slotMaxTime="24:00:00"
        // expandRows forces every slot to the same fixed height by dividing a capped container
        // height evenly across them - deceptively worked for a quiet day, but a busy half-hour
        // with several concurrent bookings needs more vertical room for its 2-line cards than a
        // quiet one, and expandRows can't give it that without also inflating every empty slot in
        // the day. Leaving it off lets each row size itself to whatever its busiest concurrent
        // column actually needs, at the cost of the day view scrolling instead of always fitting
        // one screen - the correct tradeoff once a slot's content, not just the visible range,
        // determines how tall it needs to be.
        //
        // Default FullCalendar behaviour (slotEventOverlap: true) lets overlapping-time bookings
        // cascade with a small pixel offset on top of each other, Google-Calendar-style - fine
        // for a handful of events, but with 5-7 staggered bookings across an evening it turns
        // into stacked, half-covered cards with unreadable overlapping text. false forces every
        // concurrent booking into its own non-overlapping column instead, trading some per-card
        // width for cards that are always fully visible.
        slotEventOverlap={false}
        defaultTimedEventDuration="00:30:00"
        nowIndicator
        dayCellContent={renderDayCell}
        eventContent={renderEventContent}
        eventClassNames={eventClassNames}
        eventDidMount={handleEventDidMount}
        eventClick={handleEventClick}
        height="80vh"
      />

      {selectedEvent && (
        <ReservationDetailModal
          isOpen={isModalOpen}
          onCancel={handleCloseModal}
          onConfirm={handleAccept}
          onReject={handleReject}
          onCancelReservation={handleCancel}
          onNoShow={handleNoShow}
          onClearNoShows={handleClearNoShows}
          onReminder={handleSendReminder}
          eventData={selectedEvent}
          refreshCalendar={fetchEvents}
          onProfile={() => window.open(selectedEvent.profileUrl, "_blank")}
        />
      )}

      {deleteTarget && (
        <ValidatePinModal
          key={pinAttempt}
          isOpen={true}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={handleDeleteConfirm}
          title="Delete Booking"
          subtitle={`Manager PIN required to delete ${deleteTarget.label}`}
        />
      )}
    </>
  );
};

export default CalendarView;
